// @givmo/board — every word a committee's decisions for the board say (CBG-YR6-CONFIRM; frames c2 and
// c7 of design-155, and the states no frame drew; the backend's `board-committee-confirmation.md` §17).
//
// Every line here is approved, as built: Jonathan approved them in CBG-YR6-COPY1's next part
// (2026-10-16 19:35 PDT, "go with your recs", on the copy page
// `scratchpad/2026-10-16-confirm-copy-round/copy-review.html`). A line reused word for word from a
// shipped screen says so ("Approved, reused"). A line added or changed since is marked DRAFT where
// it sits, until its own round.
//
// ⚠ The contract's §9 binds every line: Board SUGGESTS — an officer adds or puts away — and nothing is
// enforced. A committee's decision binds as recorded, so no line says a decision is confirmed,
// unconfirmed, pending, awaiting the board, effective, valid or required, and none says anything
// "must" or "should" happen. "Confirm" is said only as the organization's own practice ("…to
// confirm"), never as a state of a decision (`committees-words.test.ts` holds the list).

export const committeeConfirmationWords = {
  // ── On a board meeting (frame c7) ─────────────────────────────────────────────────────────────

  // One committee's card is titled with its name alone, as the registry records it (CBG-FIX97,
  // approved 2026-10-17 06:55 PDT): a committee's name never sits inside the title's grammar.
  // Not drawn: the card's title when several committees have decisions.
  severalTitle: 'Committee decisions',
  // Drawn c7: the organization's own practice, with where it's written — "the committee's decisions",
  // under the committee's name (CBG-FIX97, approved 2026-10-17 06:55 PDT).
  suggestionLine: (citation: string | null) =>
    `Your organization’s practice: the committee’s decisions go to the board’s next meeting to confirm${citation === null ? '' : ` (${citation})`}.`,
  // Drawn c7.
  fromMeeting: (day: string) => `From its meeting of ${day}`,
  // Not drawn: a decision taken by written consent.
  byConsent: (day: string) => `By written consent, ${day}`,
  // Drawn c7.
  carriedTally: (votesFor: number, against: number) => `Carried ${votesFor}–${against}`,
  // Not drawn: a motion recorded as carried with no tally.
  carried: 'Carried',
  // Drawn c7.
  addToAgenda: 'Add to the agenda',
  // Approved, reused (CBG-YR3-ANNUAL's card), drawn c7.
  notForThisMeeting: 'Not for this meeting',
  // Not drawn: the quiet line a dismissal leaves, which brings the card back.
  showAgain: 'Show the committee decisions',
  // Not drawn: the press's toast.
  added: 'Added to the agenda.',
  // Approved, reused (CBG-YR3-ANNUAL's card's failure).
  failed: 'We couldn’t change the agenda. Please try again.',

  // ── On the committee's page (frame c2) ────────────────────────────────────────────────────────

  // Drawn c2.
  pageTitle: 'Decisions for the board',
  // Drawn c2.
  pageLine: (citation: string | null) =>
    citation === null
      ? 'Its decisions are suggested for the board’s next meeting, to confirm.'
      : `Its decisions are suggested for the board’s next meeting, to confirm (${citation}).`,
  // Not drawn: the practice line's action.
  change: 'Change',
  // Drawn c2: a decision's day and tally ("Mar 18, 2027 · Carried 3–0").
  decidedLine: (day: string, outcome: string) => `${day} · ${outcome}`,
  // Not drawn: a decision taken by written consent, on its line.
  byConsentShort: 'By written consent',
  // Drawn c2.
  suggestedFor: (day: string) => `Suggested for the ${day} board meeting.`,
  // Not drawn in full: where a decision is on an agenda.
  onAgendaOf: (day: string) => `On the agenda of the ${day} board meeting`,
  // Not drawn: the board's motion under that item, as recorded.
  boardMotion: (outcome: 'carried' | 'failed', day: string) =>
    `The board’s motion under that item was recorded as ${outcome} on ${day}.`,
  // Not drawn: the practice on, and no decision recorded under it yet.
  noneYet: 'No decisions to suggest yet.',
  // Not drawn: the menu's way in, with the practice off.
  menuItem: 'Suggest its decisions for the board…',

  // ── The setting (its dialog, and "Add a committee") ───────────────────────────────────────────

  // The design's words, not drawn: frame c3's caption describes the Add dialog's box, but the
  // frame shows another form. The setting dialog's too.
  confirmLabel: 'Suggest its decisions for the board’s next meeting',
  // The design's words, not drawn: the executive committee form's note.
  confirmNote:
    'The executive committee form’s practice: BoardSource recommends the full board confirm an executive committee’s decisions at its next meeting. You can turn this off.',
  // Not drawn: where the organization wrote its practice down.
  citationLabel: 'Where it’s written (optional)',
  // Not drawn: the citation's hint.
  citationHint: 'For example, “Executive Committee Charter, Art. V”.',
  // Approved, reused (the committee dialogs' "Save changes").
  save: 'Save changes',
  // Approved, reused (the committee dialogs' "Changes saved.").
  saved: 'Changes saved.',
  // Shipped, reused (the self-assessment's and the officers' panel's), not drawn: a save that failed
  // with no sentence of its own.
  saveFailed: 'We couldn’t save that. Please try again.',
} as const;
